export enum TicketType {
  Change,
  Alert
}

export enum TicketState {
  Opened,
  Closed,
  InProgress,
  PendingConfirmation
}

export enum TicketContactRole {
  Administrator,
  Contact
}

export enum TicketPriority {
  Low,
  Medium,
  High,
  Critical
}

export interface TicketContact {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  date: Date;
  role: TicketContactRole;
}

interface TicketInteractDetails {
  email: string;
  firstName: string;
  id: string;
  lastName: string;
  date: Date;
}

export interface Ticket {
  id: string;
  type: TicketType;
  priority: TicketPriority;
  assetId: string; // Computer HID
  asset?: { name: string; displayName?: string; os?: unknown };
  companyId: string;
  companyName: string;
  state: TicketState;
  title: string;
  description: string;
  alertData?: string;
  created: TicketInteractDetails;
  updated?: TicketInteractDetails;
  closed?: TicketInteractDetails;
  assigneeId?: string;
  assignee?: { email: string; firstName: string; lastName: string; date: Date };
  contact?: TicketContact;
  summary: { conversations: { total: number; unread?: number } };
  lastConversation?: TicketConversation;
}

export interface ExtendedTicket extends Ticket {
  display: {
    id: string;
    closed: string;
    closedBy?: string;
    created: string;
    createdBy?: string;
    updatedBy?: string;
  };
  canDelete: boolean;
  canEdit: boolean;
  canComment: boolean;
  possibleState: {
    [TicketState.Opened]: boolean;
    [TicketState.InProgress]: boolean;
    [TicketState.PendingConfirmation]: boolean;
    [TicketState.Closed]: boolean;
  };
}

export interface TicketConversation {
  id: number;
  content: string;
  isPublic: boolean;
  isRead: boolean; // Message has been read by Current user
  created: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    date: Date;
    role: TicketContactRole;
  };
  updated?: Date;
}

export interface ExtendedTicketConversation extends TicketConversation {
  display: {
    created: string;
    updated: string;
  };
  isMyMessage: boolean;
  canDelete: boolean;
  canEdit: boolean;
}

export interface TicketList {
  data: Array<Ticket>;
  summary: {
    assignedToMe: number;
    closed: number;
    inProgress: number;
    open: number;
    pending: number;
    total: number;
  };
}

export interface TicketConversationList {
  data: Array<TicketConversation>;
  summary: { total: number };
}

export interface OATicketListQueryParams {
  assetId?: string;
  assigneeId?: string;
  companyId?: string;
  createdBy?: string;
  limit?: number;
  priority?: TicketPriority;
  offset?: number;
  orderBy?: string;
  search?: string;
  state?: Array<TicketState>;
  type?: TicketType;
}

export interface TicketListQueryParams extends OATicketListQueryParams {
  contactId?: string;
}

export interface TicketConversationListQueryParams {
  ticketId: number | string;
  limit?: number;
  offset?: number;
  sort?: string;
}

export type TicketViewData = Pick<TicketList, 'summary'> & {
  data: Array<ExtendedTicket>;
  loading?: boolean;
  loadingPage?: boolean;
  loadingColumns?: boolean;
  hasError?: boolean;
  lastFilter?: TicketListQueryParams;
};

export type UpdateTicketParams = Partial<Pick<Ticket, 'title' | 'description' | 'state' | 'assigneeId' | 'priority'>> & {
  contactId?: TicketContact['id'];
  clearContact?: boolean;
  unassign?: boolean;
};

export type BulkTicketFilter = Omit<TicketListQueryParams, 'offset' | 'limit' | 'assetId' | 'companyId'> & {
  assetId?: Array<string>;
  companyId?: Array<string>;
  excludeIds?: Array<number>;
};

export interface BulkTicketsSelector {
  ids?: Array<number>;
  filter?: BulkTicketFilter;
}

export type BulkDeleteTicketsParams = BulkTicketsSelector & { notify: boolean };

export type BulkUpdateTicketsParams = BulkDeleteTicketsParams & Pick<UpdateTicketParams, 'state' | 'priority' | 'assigneeId' | 'unassign'>;

export interface BulkTicketActionResult {
  matched: number;
  succeeded: number;
  failed: Array<{ id: number; reason: number }>;
  notProcessed: number;
}

export interface TicketNotificationSettings {
  ticketCreated: boolean;
  assigneeChanged: boolean;
  stateChanged: boolean;
  priorityChanged: boolean;
  newComment: boolean;
  detailsChanged: boolean;
}
